class FindationButton extends HHBaseElement {
  latestResults = {}
  _ready = false
  _pendingOpen = false

  constructor() {
    super()
    this._handlePostMessage = this._handlePostMessage.bind(this)
    this._handleClick = this._handleClick.bind(this)
    this.$button = this.querySelector('button')
    if (!this.isQuickShop) {
      // Keep the button interactive from the first paint. The Findation embed
      // loads asynchronously (a chain of external requests that can take several
      // seconds on a cold load); rather than disabling the button until it
      // finishes, we capture clicks immediately and queue any made before the
      // widget is ready, then open the overlay once it embeds. This removes the
      // "not clickable on first load, works on refresh" race (BM-1521).
      this._attachListeners()
      this._injectTaget()
      this._initScript()
    }
  }

  connectedCallback() {
    if (this.isQuickShop) {
      this._attachListeners()
    }
  }

  disconnectedCallback() {
    this._removeListeners()
  }

  widgetEmbedded() {
    window.__findationWidgetReady = true
    this._ready = true
    this.removeAttribute('data-loading')
    let iframe = document.getElementById('findation-button-iframe')
    iframe.setAttribute('aria-hidden', 'true')
    iframe.classList.add('hidden')
    this._attachListeners()
    // If the shopper clicked before the widget finished embedding, honour it now.
    if (this._pendingOpen) {
      this._pendingOpen = false
      this._openOverlay()
    }
    // 'Presented' is the default state
  }

  widgetDidOpen() {
    // Opened
    document.querySelector('html').classList.add('frozen')
    document.querySelector('body').classList.add('frozen')
  }

  widgetDidClose(event) {
    // Closed
    document.querySelector('html').classList.remove('frozen')
    document.querySelector('body').classList.remove('frozen')
    document.getElementById('findation-iframe').src += ''
  }

  onSearchComplete(data) {
    this.latestResults = {
      ...data,
    }
    if (data.matchSuccess) {
      crdl('event', 'findation_match_found', this.latestResults)
    } else {
      crdl('event', 'findation_no_match_found', this.latestResults)
    }
  }

  get apiKey() {
    return this.getAttribute('api-key')
  }

  get mode() {
    return this.getAttribute('mode') === 'true'
  }

  get isQuickShop() {
    return this.getAttribute('quick-shop') === 'true'
  }

  get productUrl() {
    return this.getAttribute('data-url')
  }

  _injectTaget() {
    this.target = document.createElement('div')
    this.target.id = 'findation-widget-button'
    this.appendChild(this.target)
  }

  _initScript() {
    if (document.getElementById('findation-widget-script')) {
      // Script already loaded - check if widget was previously embedded
      if (window.__findationWidgetReady) {
        // Widget already initialized, mark ready immediately
        this._ready = true
        window.addEventListener('message', this._handlePostMessage)
        this._attachListeners()
      } else {
        this._initWidget()
      }
    } else {
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.id = 'findation-widget-script'
      script.onload = () => {
        this._initWidget()
      }
      script.src = 'https://assets.findation.com/javascripts/w-adv-10.min.js'
      document.body.appendChild(script)
    }
  }

  _initWidget() {
    window.Findation = window.Findation || {}
    Findation.widgetEmbedded = this.widgetEmbedded.bind(this)
    Findation.widgetDidOpen = this.widgetDidOpen.bind(this)
    Findation.widgetDidClose = this.widgetDidClose.bind(this)
    Findation.onSearchComplete = this.onSearchComplete.bind(this)

    Findation.init(this.target, this.apiKey, {
      isDemoMode: this.mode,
    })

    window.addEventListener('message', this._handlePostMessage)

    // Safety net: if the embed callback never fires (a stalled/failed external
    // request), stop showing the loading state and flush any queued open so the
    // button is never permanently stuck. The embed normally completes well
    // within this window.
    this._readyFallback = setTimeout(() => {
      if (!this._ready) {
        this._ready = true
        this.removeAttribute('data-loading')
        if (this._pendingOpen) {
          this._pendingOpen = false
          this._openOverlay()
        }
      }
    }, 8000)

    if (location.hash === '#find-my-shade') {
      this._handleClick()
    }
  }

  _handlePostMessage(event) {
    if (!event.origin?.includes('findation.com')) return
    if (
      event.data?.eventName === 'shareFindationResults' &&
      event.data?.email
    ) {
      this._handleShareResults(event.data?.email)
    }
  }

  _handleShareResults(email) {
    if (!this.latestResults) return
    for (const key in this.latestResults) {
      this.setAttribute(`data-${key}`, JSON.stringify(this.latestResults[key]))
    }
    this._sendToCordial(email)
  }

  get _dataForCordial() {
    return {
      'Product Name': this.getAttribute('product-name'),
      'Product Shade': this.latestResults?.recommendedShade,
      'Product SKU': this.latestResults?.externalId,
      ...this.latestResults,
    }
  }

  _sendToCordial(email) {
    try {
      crdl(
        'contact',
        { email },
        {
          opt_in_source: 'findation_shade_match',
          channels: { email: { subscribeStatus: 'subscribed' } },
          recommendedShade: this.latestResults?.recommendedShade,
        }
      )
      crdl('event', 'findation_shade_match_shared', this._dataForCordial)
      console.log('Sent to cordial', {
        email,
        ...this._dataForCordial,
      })
    } catch (error) {
      console.log(error)
    }
  }

  _handleClick() {
    if (this.isQuickShop) {
      location.href = this.productUrl
    } else if (this._ready) {
      this._openOverlay()
    } else {
      // Widget still loading — remember the intent and show a loading state.
      // widgetEmbedded()/the fallback will open the overlay once ready.
      this._pendingOpen = true
      this.setAttribute('data-loading', 'true')
    }
  }

  _openOverlay() {
    window.postMessage('overlayOpen')
  }

  _attachListeners() {
    // Idempotent: the click listener is attached on first paint and again once
    // the widget embeds/was already ready. Guard so only one is ever live.
    if (this._listenersAttached) return
    this._listenersAttached = true
    this.$button.addEventListener('click', this._handleClick)
  }

  _removeListeners() {
    clearTimeout(this._readyFallback)
    this._listenersAttached = false
    window.removeEventListener('message', this._handlePostMessage)
    this.$button.removeEventListener('click', this._handleClick)
  }
}

window.customElements.define('findation-button', FindationButton)
